import { ExecutionStep, StepType } from '../types/rust-engine-interfaces';
import { step, arrayPayload, normalizeArray, rangeIds } from './fallback-helpers';

export function sortingFallback(id: string, raw: unknown): ExecutionStep[] {
  const arr = normalizeArray(raw);
  if (id.includes('merge')) return mergeSort(arr);
  if (id.includes('quick')) return quickSort(arr);
  if (id.includes('heap')) return heapSort(arr);
  if (id.includes('insertion')) return insertionSort(arr);
  if (id.includes('selection')) return selectionSort(arr);
  if (id.includes('shell')) return shellSort(arr);
  if (id.includes('counting')) return countingSort(arr);
  if (id.includes('radix')) return radixSort(arr);
  if (id.includes('bucket')) return bucketSort(arr);
  if (id.includes('cocktail') || id.includes('shaker')) return cocktailSort(arr);
  if (id.includes('gnome')) return gnomeSort(arr);
  if (id.includes('comb')) return combSort(arr);
  return bubbleSort(arr);
}

function bubbleSort(arr: number[]): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], {}), { line: 1, explanation: 'Bubble Sort starts.' }));
  const n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - i - 1; j++) {
      s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { compareIndices: [j, j + 1], highlightIndices: [j, j + 1] }), { line: 3, explanation: `Compare arr[${j}]=${arr[j]} and arr[${j + 1}]=${arr[j + 1]}.` }));
      if (arr[j]! > arr[j + 1]!) {
        [arr[j]!, arr[j + 1]!] = [arr[j + 1]!, arr[j]!];
        s.push(step(s.length, StepType.Swap, arrayPayload([...arr], { currentIndex: j, compareIndex: j + 1, highlightIndices: [j, j + 1] }), { line: 4, explanation: `Swap ${arr[j]} and ${arr[j + 1]}.` }));
      }
    }
    s.push(step(s.length, StepType.Update, arrayPayload([...arr], { sortedIndices: [n - 1 - i] }), { line: 5, explanation: `Element ${arr[n - 1 - i]} at position ${n - 1 - i} is sorted.` }));
  }
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 6, explanation: 'Array sorted.' }));
  return s;
}

function selectionSort(arr: number[]): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], {}), { line: 1, explanation: 'Selection Sort starts.' }));
  const n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    let min = i;
    s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { currentIndex: i, highlightIndices: [i] }), { line: 2, explanation: `Assume min at index ${i}.` }));
    for (let j = i + 1; j < n; j++) {
      s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { currentIndex: min, compareIndex: j, highlightIndices: [min, j] }), { line: 4, explanation: `Compare min ${arr[min]} with ${arr[j]}.` }));
      if (arr[j]! < arr[min]!) { min = j; s.push(step(s.length, StepType.Update, arrayPayload([...arr], { currentIndex: min, highlightIndices: [min] }), { line: 5, explanation: `New min at ${min}.` })); }
    }
    if (min !== i) { [arr[i]!, arr[min]!] = [arr[min]!, arr[i]!]; s.push(step(s.length, StepType.Swap, arrayPayload([...arr], { currentIndex: i, compareIndex: min, highlightIndices: [i, min] }), { line: 7, explanation: `Swap into position ${i}.` })); }
    s.push(step(s.length, StepType.Update, arrayPayload([...arr], { sortedIndices: [i] }), { line: 8, explanation: `Position ${i} is sorted.` }));
  }
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 9, explanation: 'Array sorted.' }));
  return s;
}

function insertionSort(arr: number[]): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], {}), { line: 1, explanation: 'Insertion Sort starts.' }));
  const n = arr.length;
  for (let i = 1; i < n; i++) {
    const key = arr[i]!; let j = i - 1;
    s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { currentIndex: i, highlightIndices: [i] }), { line: 2, explanation: `Select key ${key} at index ${i}.` }));
    while (j >= 0 && arr[j]! > key) {
      s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { compareIndices: [j, i], highlightIndices: [j, i] }), { line: 4, explanation: `${arr[j]} > ${key}, shift right.` }));
      arr[j + 1] = arr[j] as number; j--;
      s.push(step(s.length, StepType.Update, arrayPayload([...arr], { currentIndex: j + 1, highlightIndices: [j + 1] }), { line: 5, explanation: 'Shifted element.' }));
    }
    (arr as number[])[j + 1] = key;
    s.push(step(s.length, StepType.Update, arrayPayload([...arr], { currentIndex: j + 1, highlightIndices: [j + 1] }), { line: 6, explanation: `Inserted ${key} at index ${j + 1}.` }));
  }
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 7, explanation: 'Array sorted.' }));
  return s;
}

function mergeSort(arr: number[]): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], {}), { line: 1, explanation: 'Merge Sort starts.' }));
  function merge(l: number, m: number, r: number) {
    const temp: number[] = []; let i = l, j = m + 1;
    s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { highlightIndices: rangeIds(l, r) }), { line: 6, explanation: `Merge [${l},${m}] and [${m + 1},${r}].` }));
    while (i <= m && j <= r) {
      s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { compareIndices: [i, j], highlightIndices: [i, j] }), { line: 9, explanation: `Compare ${arr[i]} and ${arr[j]}.` }));
      if (arr[i]! <= arr[j]!) { temp.push(arr[i]!); i++; } else { temp.push(arr[j]!); j++; }
    }
    while (i <= m) { temp.push(arr[i]!); i++; }
    while (j <= r) { temp.push(arr[j]!); j++; }
    for (let k = 0; k < temp.length; k++) { arr[l + k] = temp[k]!; }
    s.push(step(s.length, StepType.Update, arrayPayload([...arr], { highlightIndices: rangeIds(l, r) }), { line: 12, explanation: `Merged range [${l},${r}].` }));
  }
  function rec(l: number, r: number) {
    if (l >= r) { s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { currentIndex: l, highlightIndices: [l] }), { line: 2, explanation: `Base case at ${l}.` })); return; }
    const m = Math.floor((l + r) / 2);
    s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { currentIndex: m, highlightIndices: rangeIds(l, r) }), { line: 3, explanation: `Divide at mid=${m}.` }));
    rec(l, m); rec(m + 1, r); merge(l, m, r);
  }
  rec(0, arr.length - 1);
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 13, explanation: 'Array sorted.' }));
  return s;
}

function quickSort(arr: number[]): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], {}), { line: 1, explanation: 'Quick Sort starts.' }));
  function partition(l: number, r: number): number {
    const pivot = arr[r]!;
    s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { pivotIndex: r, highlightIndices: rangeIds(l, r) }), { line: 3, explanation: `Pivot ${pivot} at ${r}.` }));
    let i = l - 1;
    for (let j = l; j < r; j++) {
      s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { compareIndex: j, pivotIndex: r, highlightIndices: [j, r] }), { line: 5, explanation: `Compare ${arr[j]} with pivot ${pivot}.` }));
      if (arr[j]! <= pivot) { i++; [arr[i]!, arr[j]!] = [arr[j]!, arr[i]!]; s.push(step(s.length, StepType.Swap, arrayPayload([...arr], { currentIndex: i, compareIndex: j, highlightIndices: [i, j] }), { line: 6, explanation: `Swap to index ${i}.` })); }
    }
    [arr[i + 1]!, arr[r]!] = [arr[r]!, arr[i + 1]!];
    s.push(step(s.length, StepType.Swap, arrayPayload([...arr], { currentIndex: i + 1, compareIndex: r, highlightIndices: [i + 1, r] }), { line: 8, explanation: `Place pivot at ${i + 1}.` }));
    return i + 1;
  }
  function rec(l: number, r: number) { if (l >= r) return; const p = partition(l, r); rec(l, p - 1); rec(p + 1, r); }
  rec(0, arr.length - 1);
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 9, explanation: 'Array sorted.' }));
  return s;
}

function heapSort(arr: number[]): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], {}), { line: 1, explanation: 'Heap Sort starts.' }));
  const n = arr.length;
  function heapify(size: number, root: number) {
    let largest = root, l = 2 * root + 1, r = 2 * root + 2;
    if (l < size && arr[l]! > arr[largest]!) largest = l;
    if (r < size && arr[r]! > arr[largest]!) largest = r;
    if (largest !== root) {
      s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { compareIndices: [root, largest], highlightIndices: [root, largest] }), { line: 4, explanation: `Heapify: swap ${arr[root]} with ${arr[largest]}.` }));
      const _tmp = arr[root] as number; (arr as number[])[root] = arr[largest] as number; (arr as number[])[largest] = _tmp;
      s.push(step(s.length, StepType.Swap, arrayPayload([...arr], { currentIndex: root, compareIndex: largest, highlightIndices: [root, largest] }), { line: 5, explanation: 'Swapped.' }));
      heapify(size, largest);
    }
  }
  for (let i = Math.floor(n / 2) - 1; i >= 0; i--) { s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { currentIndex: i, highlightIndices: [i] }), { line: 7, explanation: `Build heap from ${i}.` })); heapify(n, i); }
  for (let i = n - 1; i > 0; i--) { [arr[0]!, arr[i]!] = [arr[i]!, arr[0]!]; s.push(step(s.length, StepType.Swap, arrayPayload([...arr], { currentIndex: 0, compareIndex: i, sortedIndices: rangeIds(i + 1, n - 1), highlightIndices: [0, i] }), { line: 9, explanation: `Move max to position ${i}.` })); heapify(i, 0); }
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 11, explanation: 'Array sorted.' }));
  return s;
}

function shellSort(arr: number[]): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], {}), { line: 1, explanation: 'Shell Sort starts.' }));
  const n = arr.length;
  for (let gap = Math.floor(n / 2); gap > 0; gap = Math.floor(gap / 2)) {
    s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], {}), { line: 2, explanation: `Gap = ${gap}.` }));
    for (let i = gap; i < n; i++) {
      const temp = arr[i]; let j = i;
      while (j >= gap && arr[j - gap]! > temp!) { s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { compareIndices: [j - gap, i], highlightIndices: [j - gap, i] }), { line: 5, explanation: `${arr[j - gap]} > ${temp}, shift.` })); arr[j] = arr[j - gap]!; j -= gap; s.push(step(s.length, StepType.Update, arrayPayload([...arr], { currentIndex: j + gap, highlightIndices: [j + gap] }), { line: 6, explanation: 'Shifted.' })); }
      arr[j] = temp!; s.push(step(s.length, StepType.Update, arrayPayload([...arr], { currentIndex: j, highlightIndices: [j] }), { line: 7, explanation: `Insert ${temp} at ${j}.` }));
    }
  }
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 8, explanation: 'Array sorted.' }));
  return s;
}

function countingSort(arr: number[]): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], {}), { line: 1, explanation: 'Counting Sort starts.' }));
  const max = Math.max(...arr); const count = Array(max + 1).fill(0);
  arr.forEach((v, i) => { s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { currentIndex: i, highlightIndices: [i] }), { line: 3, explanation: `Count ${v}.` })); count[v]++; });
  let idx = 0;
  for (let i = 0; i <= max; i++) { while (count[i] > 0) { arr[idx] = i; count[i]--; s.push(step(s.length, StepType.Update, arrayPayload([...arr], { currentIndex: idx, highlightIndices: [idx] }), { line: 5, explanation: `Place ${i} at ${idx}.` })); idx++; } }
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 6, explanation: 'Array sorted.' }));
  return s;
}

function radixSort(arr: number[]): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], {}), { line: 1, explanation: 'Radix Sort starts.' }));
  const max = Math.max(...arr);
  for (let exp = 1; Math.floor(max / exp) > 0; exp *= 10) {
    s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], {}), { line: 2, explanation: `Sort by digit place ${exp}.` }));
    const n = arr.length; const out = Array(n).fill(0); const count = Array(10).fill(0);
    for (let i = 0; i < n; i++) { const d = Math.floor(arr[i]! / exp) % 10; count[d]++; s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { currentIndex: i, highlightIndices: [i] }), { line: 5, explanation: `Count digit ${d}.` })); }
    for (let i = 1; i < 10; i++) count[i] += count[i - 1];
    for (let i = n - 1; i >= 0; i--) { const d = Math.floor(arr[i]! / exp) % 10; out[count[d] - 1] = arr[i]!; count[d]--; s.push(step(s.length, StepType.Update, arrayPayload([...arr], { currentIndex: i, highlightIndices: [i] }), { line: 7, explanation: `Place by digit ${d}.` })); }
    for (let i = 0; i < n; i++) arr[i] = out[i]!;
    s.push(step(s.length, StepType.Update, arrayPayload([...arr], { highlightIndices: arr.map((_, i) => i) }), { line: 9, explanation: `After digit ${exp}.` }));
  }
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 10, explanation: 'Array sorted.' }));
  return s;
}

function bucketSort(arr: number[]): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], {}), { line: 1, explanation: 'Bucket Sort starts.' }));
  const n = arr.length; const max = Math.max(...arr); const min = Math.min(...arr); const range = Math.max(1, max - min);
  const buckets: number[][] = Array.from({ length: n }, () => []);
  arr.forEach((v, i) => { const b = Math.floor(((v - min) / range) * (n - 1)); buckets[b]!.push(v); s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { currentIndex: i, highlightIndices: [i] }), { line: 3, explanation: `Place ${v} into bucket ${b}.` })); });
  let idx = 0;
  buckets.forEach((bucket, bi) => { bucket.sort((a, b) => a - b); bucket.forEach((v) => { arr[idx] = v; s.push(step(s.length, StepType.Update, arrayPayload([...arr], { currentIndex: idx, highlightIndices: [idx] }), { line: 5, explanation: `Empty bucket ${bi}: place ${v}.` })); idx++; }); });
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 7, explanation: 'Array sorted.' }));
  return s;
}

function cocktailSort(arr: number[]): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], {}), { line: 1, explanation: 'Cocktail Shaker Sort starts.' }));
  let swapped = true, start = 0, end = arr.length - 1;
  while (swapped) {
    swapped = false;
    for (let i = start; i < end; i++) {
      s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { compareIndices: [i, i + 1], highlightIndices: [i, i + 1] }), { line: 3, explanation: `Compare ${arr[i]} and ${arr[i + 1]}.` }));
      if (arr[i]! > arr[i + 1]!) { [arr[i]!, arr[i + 1]!] = [arr[i + 1]!, arr[i]!]; swapped = true; s.push(step(s.length, StepType.Swap, arrayPayload([...arr], { currentIndex: i, compareIndex: i + 1, highlightIndices: [i, i + 1] }), { line: 4, explanation: `Swap forward. Element ${arr[i]} moved up.` })); }
    }
    if (!swapped) break;
    end--;
    swapped = false;
    for (let i = end; i > start; i--) {
      s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { compareIndices: [i - 1, i], highlightIndices: [i - 1, i] }), { line: 6, explanation: `Compare ${arr[i - 1]} and ${arr[i]}.` }));
      if (arr[i - 1]! > arr[i]!) { [arr[i - 1]!, arr[i]!] = [arr[i]!, arr[i - 1]!]; swapped = true; s.push(step(s.length, StepType.Swap, arrayPayload([...arr], { currentIndex: i, compareIndex: i - 1, highlightIndices: [i - 1, i] }), { line: 7, explanation: `Swap backward. Element ${arr[i]} moved down.` })); }
    }
    start++;
  }
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 9, explanation: 'Array sorted.' }));
  return s;
}

function gnomeSort(arr: number[]): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], {}), { line: 1, explanation: 'Gnome Sort starts.' }));
  let i = 0;
  while (i < arr.length) {
    if (i === 0 || arr[i]! >= arr[i - 1]!) {
      const compareIndices = i === 0 ? [0] : [i - 1, i];
      const highlightIndices = i === 0 ? [0] : [i - 1, i];
      const explanation = i === 0 ? `${arr[i]} at position 0, move forward.` : `${arr[i]} >= ${arr[i - 1]}, move forward.`;
      s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { compareIndices, highlightIndices }), { line: 3, explanation }));
      i++;
    } else {
      s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { compareIndices: [i - 1, i], highlightIndices: [i - 1, i] }), { line: 4, explanation: `${arr[i]} < ${arr[i - 1]}, swap backward.` }));
      [arr[i]!, arr[i - 1]!] = [arr[i - 1]!, arr[i]!];
      s.push(step(s.length, StepType.Swap, arrayPayload([...arr], { currentIndex: i - 1, compareIndex: i, highlightIndices: [i - 1, i] }), { line: 5, explanation: 'Swapped.' }));
      i--;
    }
  }
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 6, explanation: 'Array sorted.' }));
  return s;
}

function combSort(arr: number[]): ExecutionStep[] {
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], {}), { line: 1, explanation: 'Comb Sort starts.' }));
  const n = arr.length; let gap = n; let swapped = true; const shrink = 1.3;
  while (gap > 1 || swapped) {
    gap = Math.max(1, Math.floor(gap / shrink));
    swapped = false;
    for (let i = 0; i + gap < n; i++) {
      s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { compareIndices: [i, i + gap], highlightIndices: [i, i + gap] }), { line: 3, explanation: `Compare gap ${gap}: ${arr[i]} vs ${arr[i + gap]}.` }));
      if (arr[i]! > arr[i + gap]!) { [arr[i]!, arr[i + gap]!] = [arr[i + gap]!, arr[i]!]; swapped = true; s.push(step(s.length, StepType.Swap, arrayPayload([...arr], { currentIndex: i, compareIndex: i + gap, highlightIndices: [i, i + gap] }), { line: 4, explanation: 'Swap.' })); }
    }
  }
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 6, explanation: 'Array sorted.' }));
  return s;
}
